import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { Suspense, useState } from "react";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchUsersQuery } from "@/datasource/http/users/query";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { PhoneHelpers } from "@/lib/phone";
import type { User, UserRole } from "@/models/user";

export interface UserAutocompleteProps {
  placeholder?: string;
  value?: User | null;
  onSelect?: (user: User | null) => void;
  userRole?: UserRole;
  includeLoyaltyAccount?: boolean;
  showPoints?: boolean;
  disabled?: boolean;
  error?: boolean;
}

function UsersList({
  search,
  userRole,
  includeLoyaltyAccount,
  selectedId,
  showPoints,
  onSelect,
}: {
  search: string;
  userRole?: UserRole;
  includeLoyaltyAccount?: boolean;
  selectedId?: number;
  showPoints?: boolean;
  onSelect: (user: User) => void;
}) {
  const { data } = useSearchUsersQuery({
    search,
    role: userRole,
    include: includeLoyaltyAccount ? "loyalty_account" : undefined,
    limit: 10,
  });

  const users = data.data.users;

  if (users.length === 0) {
    return <CommandEmpty>No users found.</CommandEmpty>;
  }

  return (
    <CommandGroup>
      {users.map((user) => (
        <CommandItem
          key={user.id}
          value={user.id.toString()}
          onSelect={() => onSelect(user)}
          className="flex items-center gap-2"
        >
          <Avatar name={user.name} className="h-8 w-8" />
          <div className="flex flex-col min-w-0 flex-1">
            <span className="font-medium truncate">{user.name}</span>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground font-mono">
                {PhoneHelpers.format(user.phone_number)}
              </span>
              {user.loyalty_code && (
                <>
                  <span className="rounded-full size-1 bg-gray-400" />
                  <span className="text-xs text-muted-foreground font-mono">
                    CODE: {user.loyalty_code}
                  </span>
                </>
              )}
            </div>
          </div>
          {showPoints && user.loyalty_account && (
            <Badge variant="outline">{user.loyalty_account.current_points} pts</Badge>
          )}
          {selectedId === user.id && <CheckIcon size={16} className="ml-auto" />}
        </CommandItem>
      ))}
    </CommandGroup>
  );
}

function UsersListSkeleton() {
  return (
    <CommandGroup>
      {[0, 1, 2, 3, 4].map((skeletonId) => (
        <div key={skeletonId} className="flex items-center gap-2 px-2 py-1.5">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div className="flex flex-col min-w-0 flex-1 gap-1">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      ))}
    </CommandGroup>
  );
}

export function UserAutocomplete({
  placeholder = "Search by name, phone or loyalty code...",
  value,
  onSelect,
  userRole,
  includeLoyaltyAccount = false,
  showPoints = false,
  disabled = false,
  error = false,
}: UserAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchQuery, 500);

  const handleSelect = (user: User) => {
    setOpen(false);
    onSelect?.(user);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between border-input bg-background px-3 font-normal outline-offset-0 outline-none hover:bg-background focus-visible:outline-[3px] ${
            error ? "border-destructive" : ""
          }`}
          disabled={disabled}
        >
          {value ? (
            <span className="flex min-w-0 items-center gap-2">
              <Avatar name={value.name} className="h-6 w-6" />
              <span className="truncate">{value.name}</span>
              {value.loyalty_code && (
                <span className="text-muted-foreground font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                  {value.loyalty_code}
                </span>
              )}
              {showPoints && value.loyalty_account && (
                <Badge variant="secondary" className="ml-1">
                  {value.loyalty_account.current_points} pts
                </Badge>
              )}
              <span className="text-muted-foreground font-mono text-xs">
                {PhoneHelpers.format(value.phone_number)}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronDownIcon
            size={16}
            className="shrink-0 text-muted-foreground/80"
            aria-hidden="true"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full min-w-(--radix-popper-anchor-width) border-input p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {open && (
              <Suspense fallback={<UsersListSkeleton />}>
                <UsersList
                  search={debouncedSearch}
                  userRole={userRole}
                  includeLoyaltyAccount={includeLoyaltyAccount}
                  selectedId={value?.id}
                  showPoints={showPoints}
                  onSelect={handleSelect}
                />
              </Suspense>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
